import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const EMPTY = { title: "", description: "", cover: "", date: "", time: "", location: "", cta_label: "", cta_link: "", expiry: "", active: true };

const I = ({ label, value, onChange, type = "text", placeholder }) => (
  <div>
    {label && <label className="block text-xs font-semibold mb-1 text-slate-600">{label}</label>}
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-9 px-3 rounded-lg bg-muted text-sm outline-none focus:ring-2 ring-primary" />
  </div>
);

export const EventsManager = ({ businessId }) => {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/events-all/${businessId}`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { if (businessId) load(); }, [businessId]); // eslint-disable-line

  const save = async () => {
    const payload = { ...editing, business_id: businessId };
    try {
      if (editing.id) await api.put(`/events/${editing.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Αποθηκεύτηκε");
      setEditing(null);
      load();
    } catch { toast.error("Σφάλμα αποθήκευσης"); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };

  if (!businessId) {
    return <p className="text-sm text-slate-500" data-testid="events-need-save">Αποθήκευσε πρώτα την επιχείρηση για να προσθέσεις Νέα / Εκδηλώσεις.</p>;
  }

  return (
    <div className="space-y-3" data-testid="events-manager">
      {!editing && (
        <>
          <div className="space-y-2">
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg border border-border p-2" data-testid="admin-event-row">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{e.title}</p>
                  <p className="text-xs text-slate-500">{e.date}{e.time ? ` · ${e.time}` : ""}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(e)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-primary"><Pencil size={14} /></button>
                  <button onClick={() => del(e.id)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-destructive"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {events.length === 0 && <p className="text-sm text-slate-400">Δεν υπάρχουν events.</p>}
          </div>
          <button type="button" onClick={() => setEditing({ ...EMPTY })} data-testid="add-event" className="text-sm font-semibold text-primary flex items-center gap-1"><Plus size={14} /> Προσθήκη event</button>
        </>
      )}

      {editing && (
        <div className="rounded-xl border border-border p-3 space-y-2" data-testid="event-editor">
          <I label="Τίτλος" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <ImageUpload label="Cover εικόνα" value={editing.cover} onChange={(v) => setEditing({ ...editing, cover: v })} />
          <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Περιγραφή" className="w-full px-3 py-2 rounded-lg bg-muted text-sm outline-none resize-none" />
          <div className="grid grid-cols-2 gap-2">
            <I label="Ημερομηνία" type="date" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
            <I label="Ώρα" value={editing.time} onChange={(v) => setEditing({ ...editing, time: v })} placeholder="18:00" />
            <I label="Τοποθεσία" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
            <I label="Λήξη (μετά → προηγούμενα)" type="date" value={editing.expiry} onChange={(v) => setEditing({ ...editing, expiry: v })} />
            <I label="CTA κείμενο" value={editing.cta_label} onChange={(v) => setEditing({ ...editing, cta_label: v })} placeholder="Εγγραφή" />
            <I label="CTA link" value={editing.cta_link} onChange={(v) => setEditing({ ...editing, cta_link: v })} placeholder="https://..." />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" onClick={save} className="rounded-full" data-testid="event-save">Αποθήκευση</Button>
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Άκυρο</Button>
          </div>
        </div>
      )}
    </div>
  );
};
